import { useState } from "react";
import { Download, Calendar, ChevronDown, ChevronLeft, ChevronRight, FileSpreadsheet, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ExportService } from "@/integrations/api/services/exportService";
import { AuthService } from "@/integrations/api/services/authService";
import LmsOverdueUpload from "@/components/reports/LmsOverdueUpload";
import { toast } from "sonner";

interface CollectionExportDialogProps {
  onExportComplete?: () => void;
  triggerClassName?: string;
  iconClassName?: string;
  label?: string;
}

type ReportType =
  | 'collection-data'
  | 'visit-data'
  | 'transaction-data'
  | 'calling-activity'
  | 'attendance'
  | 'cash-collection'
  | 'ptp-visit'
  | 'repayment_schedule';

function InlineSelect({
  placeholder,
  value,
  options,
  onChange,
}: {
  placeholder: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);

  return (
    <div>
      <button
        type="button"
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <span className={selected ? "text-gray-900" : "text-muted-foreground"}>
          {selected?.label || placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 max-h-40 w-full overflow-y-auto rounded-md border border-gray-200 bg-white">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                option.value === value ? "bg-gray-100 font-medium" : ""
              }`}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function toIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function ReportDownloadList({ onSelect }: { onSelect: (type: ReportType) => void }) {
  const reports: { type: ReportType; title: string; description: string }[] = [
    { type: "collection-data", title: "Collection Data", description: "Export collection and payment data" },
    { type: "visit-data", title: "Visit Data", description: "Export field visit information" },
    { type: "transaction-data", title: "Transaction Report", description: "Export transaction data for the selected month and year" },
    { type: "calling-activity", title: "Calling Activity", description: "Export calling activity (date range)" },
    { type: "attendance", title: "Attendance", description: "Export attendance data (date range)" },
    { type: "cash-collection", title: "Cash Collection", description: "Export cash collection data for the selected month and year" },
    { type: "ptp-visit", title: "PTP Visit", description: "Export PTP visit data for the selected month and year" },
    { type: "repayment_schedule", title: "Repayment Schedule", description: "Export repayment schedule for the selected demand month and year" },
  ];

  return (
    <div className="space-y-3">
      {reports.map((report) => (
        <Button
          key={report.type}
          variant="outline"
          className="h-auto min-h-16 w-full justify-start whitespace-normal py-3 text-left text-base"
          onClick={() => onSelect(report.type)}
        >
          <div className="flex min-w-0 flex-col items-start">
            <span className="font-semibold">{report.title}</span>
            <span className="text-xs font-normal text-gray-500">{report.description}</span>
          </div>
        </Button>
      ))}
    </div>
  );
}

function InlineDatePicker({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? new Date(`${value}T00:00:00`) : null;
  const [view, setView] = useState(() => selected ?? new Date());

  const year = view.getFullYear();
  const month = view.getMonth();
  const leading = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: { date: Date; inMonth: boolean }[] = [];

  for (let i = 0; i < leading; i++) {
    cells.push({ date: new Date(year, month, 1 - (leading - i)), inMonth: false });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ date: new Date(year, month, day), inMonth: true });
  }
  while (cells.length % 7 !== 0) {
    const last = cells[cells.length - 1].date;
    const next = new Date(last);
    next.setDate(last.getDate() + 1);
    cells.push({ date: next, inMonth: false });
  }

  const label = selected
    ? selected.toLocaleDateString("en-GB")
    : "dd/mm/yyyy";

  return (
    <div>
      <button
        id={id}
        type="button"
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <span className={selected ? "text-gray-900" : "text-muted-foreground"}>{label}</span>
        <Calendar className="h-4 w-4 opacity-50" />
      </button>
      {open && (
        <div className="mt-1 w-full rounded-md border border-gray-200 bg-white p-2">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              className="rounded p-1 hover:bg-gray-100"
              onClick={() => setView(new Date(year, month - 1, 1))}
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium">
              {view.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
            </span>
            <button
              type="button"
              className="rounded p-1 hover:bg-gray-100"
              onClick={() => setView(new Date(year, month + 1, 1))}
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 text-center text-xs text-gray-500">
            {["M", "T", "W", "T", "F", "S", "S"].map((day, index) => (
              <div key={`${day}-${index}`} className="py-1">{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 text-center text-sm">
            {cells.map((cell) => {
              const iso = toIsoDate(cell.date);
              const isSelected = value === iso;
              return (
                <button
                  key={iso}
                  type="button"
                  className={`rounded py-1 ${
                    isSelected
                      ? "bg-blue-600 text-white"
                      : cell.inMonth
                        ? "text-gray-900 hover:bg-gray-100"
                        : "text-gray-300 hover:bg-gray-50"
                  }`}
                  onClick={() => {
                    onChange(iso);
                    setOpen(false);
                  }}
                >
                  {cell.date.getDate()}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex justify-between px-1 text-xs text-blue-600">
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                onChange(toIsoDate(today));
                setView(today);
                setOpen(false);
              }}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const CollectionExportDialog = ({
  onExportComplete,
  triggerClassName,
  iconClassName = "h-4 w-4",
  label,
}: CollectionExportDialogProps) => {
  const [isReportTypeDialogOpen, setIsReportTypeDialogOpen] = useState(false);
  const [reportPanel, setReportPanel] = useState<"download" | "upload">("download");
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isLmsUploadDialogOpen, setIsLmsUploadDialogOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [isExporting, setIsExporting] = useState(false);
  const [reportType, setReportType] = useState<ReportType | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const monthOptions = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  // Generate year options (current year and previous 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => ({
    value: (currentYear - i).toString(),
    label: (currentYear - i).toString()
  }));

  const handleReportTypeSelect = (type: ReportType) => {
    setReportType(type);
    setSelectedMonth("");
    setSelectedYear("");
    setIsReportTypeDialogOpen(false);
    // Open the export dialog after a short delay
    setTimeout(() => {
      setIsExportDialogOpen(true);
    }, 100);
  };

  const handleReportButtonClick = () => {
    setReportPanel("download");
    setIsReportTypeDialogOpen(true);
  };

  const handleLmsOverdueSelect = () => {
    setIsReportTypeDialogOpen(false);
    setTimeout(() => {
      setIsLmsUploadDialogOpen(true);
    }, 100);
  };

  const canUploadLmsOverdue = AuthService.isAdmin();

  const handleExport = async () => {
    if (!reportType) return;
    const needsDateRange =
      reportType === 'calling-activity' ||
      reportType === 'attendance';
    const needsMonthYear =
      reportType === 'collection-data' ||
      reportType === 'visit-data' ||
      reportType === 'ptp-visit' ||
      reportType === 'transaction-data' ||
      reportType === 'cash-collection' ||
      reportType === 'repayment_schedule';

    if (needsDateRange) {
      if (!startDate || !endDate) {
        toast.error("Please select start and end dates");
        return;
      }
    } else if (needsMonthYear) {
      if (!selectedMonth || !selectedYear) {
        toast.error("Please select both month and year");
        return;
      }
    }

    setIsExporting(true);
    try {
      if (needsDateRange) {
        await ExportService.exportAndDownload({
          report_type: reportType,
          start_date: startDate,
          end_date: endDate,
          format: 'excel'
        });
      } else if (needsMonthYear) {
        await ExportService.exportAndDownload({
          report_type: reportType,
          demand_month: parseInt(selectedMonth),
          demand_year: parseInt(selectedYear),
          format: 'excel'
        });
      }
      
      toast.success("Export completed successfully!");
      setIsExportDialogOpen(false);
      setReportType(null);
      setSelectedMonth("");
      setSelectedYear("");
      setStartDate("");
      setEndDate("");
      onExportComplete?.();
    } catch (error) {
      console.error('Export failed:', error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportDialogClose = (open: boolean) => {
    setIsExportDialogOpen(open);
    if (!open) {
      setReportType(null);
      setSelectedMonth("");
      setSelectedYear("");
    }
  };

  const canExport =
    (reportType === 'calling-activity' ||
      reportType === 'attendance')
      ? Boolean(startDate && endDate && !isExporting)
      : reportType === 'collection-data' ||
          reportType === 'visit-data' ||
          reportType === 'ptp-visit' ||
          reportType === 'transaction-data' ||
          reportType === 'cash-collection' ||
          reportType === 'repayment_schedule'
        ? Boolean(selectedMonth && selectedYear && !isExporting)
        : false;

  const getReportTypeLabel = (type: ReportType) => {
    if (type === 'collection-data') return 'Collection Data';
    if (type === 'visit-data') return 'Visit Data';
    if (type === 'transaction-data') return 'Transaction Report';
    if (type === 'calling-activity') return 'Calling Activity';
    if (type === 'attendance') return 'Attendance';
    if (type === 'cash-collection') return 'Cash Collection';
    if (type === 'ptp-visit') return 'PTP Visit';
    return 'Repayment Schedule';
  };

  return (
    <>
      <button
        type="button"
        className={triggerClassName}
        aria-label={label || "Reports"}
        onClick={handleReportButtonClick}
      >
        <FileSpreadsheet className={`${iconClassName} shrink-0`} />
        {label}
      </button>

      <Dialog
        open={isReportTypeDialogOpen}
        onOpenChange={(open) => {
          setIsReportTypeDialogOpen(open);
          if (!open) setReportPanel("download");
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-x-hidden overflow-y-auto sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {reportPanel === "upload" ? <Upload className="h-4 w-4" /> : <Download className="h-4 w-4" />}
              Reports
            </DialogTitle>
          </DialogHeader>
          {canUploadLmsOverdue ? (
            <Tabs
              value={reportPanel}
              onValueChange={(value) => setReportPanel(value as "download" | "upload")}
            >
              <TabsList className="grid h-9 w-full grid-cols-2">
                <TabsTrigger value="download" className="gap-1.5 text-sm">
                  <Download className="h-3.5 w-3.5" />
                  Download
                </TabsTrigger>
                <TabsTrigger value="upload" className="gap-1.5 text-sm">
                  <Upload className="h-3.5 w-3.5" />
                  Upload
                </TabsTrigger>
              </TabsList>
              <TabsContent value="download" className="mt-3">
                <ReportDownloadList onSelect={handleReportTypeSelect} />
              </TabsContent>
              <TabsContent value="upload" className="mt-3">
                <Button
                  variant="outline"
                  className="h-auto min-h-16 w-full justify-start whitespace-normal py-3 text-left text-base"
                  onClick={handleLmsOverdueSelect}
                >
                  <div className="flex min-w-0 flex-col items-start">
                    <span className="font-semibold">LMS Overdue Sync</span>
                    <span className="text-xs font-normal text-gray-500">Upload overdue EMI and charges and update loan details</span>
                  </div>
                </Button>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="py-2">
              <ReportDownloadList onSelect={handleReportTypeSelect} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isLmsUploadDialogOpen} onOpenChange={setIsLmsUploadDialogOpen}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100%-1.5rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl">
          <DialogHeader className="shrink-0 space-y-1 border-b px-6 py-4 pr-12 text-left">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Upload className="h-4 w-4" />
              LMS Overdue Sync
            </DialogTitle>
            <DialogDescription className="text-xs leading-5">
              Upload overdue EMI and charges from LMS. This updates loan details for the matching loans.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 py-5">
            <LmsOverdueUpload />
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog - Month/Year Selection */}
      <Dialog open={isExportDialogOpen} onOpenChange={handleExportDialogClose}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Export {reportType ? getReportTypeLabel(reportType) : 'Report'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">{reportType ? getReportTypeLabel(reportType) : 'Report'} Export</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <CardDescription className="text-xs mb-4">
                  {reportType === 'collection-data' && 'Export collection data including applicant details, payment information, collection status, and recent comments for the selected month and year.'}
                  {reportType === 'visit-data' && 'Export visit data including field visit details, visit status, and related information for the selected month and year.'}
                  {reportType === 'transaction-data' && 'Export transaction data for the selected month and year.'}
                  {reportType === 'calling-activity' && 'Export calling activity between a start and end date.'}
                  {reportType === 'attendance' && 'Export attendance data between a start and end date.'}
                  {reportType === 'cash-collection' && 'Export cash collection data for the selected month and year.'}
                  {reportType === 'ptp-visit' && 'Export PTP visit data for the selected month and year.'}
                  {reportType === 'repayment_schedule' && 'Export the repayment schedule for the selected demand month and year.'}
                </CardDescription>
              
              <div className="space-y-4">
                {reportType === 'collection-data' || reportType === 'visit-data' || reportType === 'ptp-visit' || reportType === 'transaction-data' || reportType === 'cash-collection' || reportType === 'repayment_schedule' ? (
                  <>
                    <div className="space-y-2">
                      <Label>Select Month</Label>
                      <InlineSelect
                        placeholder="Choose month"
                        value={selectedMonth}
                        options={monthOptions}
                        onChange={setSelectedMonth}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Select Year</Label>
                      <InlineSelect
                        placeholder="Choose year"
                        value={selectedYear}
                        options={yearOptions}
                        onChange={setSelectedYear}
                      />
                    </div>
                  </>
                ) : reportType === 'calling-activity' ||
                  reportType === 'attendance' ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="start-date">Start Date</Label>
                      <InlineDatePicker id="start-date" value={startDate} onChange={setStartDate} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end-date">End Date</Label>
                      <InlineDatePicker id="end-date" value={endDate} onChange={setEndDate} />
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-gray-600">
                    No additional filters required for this report type.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setIsExportDialogOpen(false)} 
              size="sm"
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleExport} 
              size="sm" 
              disabled={!canExport}
              className="min-w-[100px]"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-3 w-3 mr-1" />
                  Export Excel
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default CollectionExportDialog;
